import { useAxiosConfig } from '@shared/api/AxiosConfigProvider';
import {
  getAppBaseFoodsListQueryKey,
  getAppCollectionsListQueryKey,
  getAppIngredientPickerListQueryKey,
  getAppProductsListQueryKey,
  getAppProductsResultRetrieveQueryKey,
  useAppProductsCsvRetrieve,
  useAppProductsDestroy,
  useAppProductsDuplicateCreate,
  useAppProductsEditCreate,
  useAppProductsList,
  useAppProductsListInfinite,
  useAppProductsResultRetrieve,
  useAppProductsSubmitCreate,
} from '@shared/api/lib/app/app';
import {
  getProductsRetrieveQueryKey,
  useProductsAssessCreate,
  useProductsBulkImportCreate,
  useProductsCreate,
  useProductsPartialUpdate,
  useProductsRetrieve,
} from '@shared/api/lib/products/products';
import {
  AppProductsListParams,
  PaginatedProductListList,
  ProductResult,
  ProductWithRelations,
} from '@shared/api/types';
import { useQueryClient } from '@tanstack/react-query';

export const useGetProductsList = (params?: AppProductsListParams) =>
  useAppProductsList<PaginatedProductListList>(params, {
    request: useAxiosConfig(),
    query: {
      keepPreviousData: true,
      cacheTime: 1000 * 60 * 60 * 24,
    },
  });

export const useGetInfiniteProductsList = (params?: AppProductsListParams) => {
  return useAppProductsListInfinite<PaginatedProductListList>(params, {
    request: useAxiosConfig(),
    query: {
      getNextPageParam: (lastPage, pages) => {
        return lastPage.next ? pages.length + 1 : undefined;
      },
    },
  });
};

export const useDeleteProduct = () => {
  const queryClient = useQueryClient();
  return useAppProductsDestroy({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries(getAppProductsListQueryKey());
        await queryClient.invalidateQueries(
          getAppIngredientPickerListQueryKey()
        );
      },
    },
  });
};

export const useEditProduct = () => {
  const queryClient = useQueryClient();
  return useAppProductsEditCreate({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries([getAppProductsListQueryKey()]);
        await queryClient.invalidateQueries(
          getAppIngredientPickerListQueryKey()
        );
      },
    },
  });
};

export const useCreateProduct = () => {
  const queryClient = useQueryClient();
  return useProductsCreate({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: async (data) => {
        await queryClient.invalidateQueries([
          getAppBaseFoodsListQueryKey(),
          getAppProductsListQueryKey(),
          getProductsRetrieveQueryKey(data.uuid),
        ]);
        await queryClient.invalidateQueries(
          getAppIngredientPickerListQueryKey()
        );
      },
    },
  });
};

export const useUpdateProduct = () => {
  const queryClient = useQueryClient();
  return useProductsPartialUpdate<ProductResult>({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: async (data) => {
        queryClient.setQueryData(getProductsRetrieveQueryKey(data.uuid), data);
        await queryClient.invalidateQueries(
          getAppProductsResultRetrieveQueryKey(data.uuid)
        );
        await queryClient.invalidateQueries([getAppProductsListQueryKey()]);
        await queryClient.invalidateQueries(
          getAppIngredientPickerListQueryKey()
        );
      },
    },
  });
};

export const useGetProduct = (productUuid: string) => {
  return useProductsRetrieve<ProductWithRelations>(productUuid, {
    request: useAxiosConfig(),
    query: {
      enabled: !!productUuid,
    },
  });
};
export const useAssessProduct = () => {
  const queryClient = useQueryClient();
  return useProductsAssessCreate({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries(getAppProductsListQueryKey());
        await queryClient.invalidateQueries(
          getAppIngredientPickerListQueryKey()
        );
      },
    },
  });
};

export const useGetProductResult = (productUuid: string) =>
  useAppProductsResultRetrieve<ProductResult>(productUuid, {
    request: useAxiosConfig(),
    query: {
      enabled: !!productUuid,
    },
  });

export const useSubmitProductAssessment = () => {
  return useAppProductsSubmitCreate({
    request: useAxiosConfig(),
  });
};

export const useGetProductsCSVExport = (
  enabled: boolean,
  filters?: {
    collection?: string;
    search?: string;
    emissions_category?: string[];
    status?: string[];
    sort?: string[];
  }
) =>
  useAppProductsCsvRetrieve({
    request: {
      ...useAxiosConfig(),
      params: filters,
    },
    query: {
      enabled,
    },
  });

export const useDuplicateProduct = () => {
  const queryClient = useQueryClient();
  return useAppProductsDuplicateCreate({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries(getAppProductsListQueryKey());
        await queryClient.invalidateQueries(
          getAppIngredientPickerListQueryKey()
        );
      },
    },
  });
};

export const useBulkImportProducts = () => {
  const queryClient = useQueryClient();
  return useProductsBulkImportCreate({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries(getAppCollectionsListQueryKey());
      },
    },
  });
};
